/**
 * BookEditor/index.jsx
 * 
 * Main Book Editor container component.
 * Provides the three-panel layout: PageList | PageCanvas | LayoutPicker
 * 
 * Features:
 * - Drag-and-drop page reordering
 * - Layout selection for selected page
 * - Auto-save with debouncing
 * - Keyboard shortcuts (Cmd+Z, Cmd+Shift+Z)
 * 
 * FIX (Jan 23, 2026): Added entry cache sync between useBookDraft and EditorContext
 */

import React, { useEffect, useCallback, useRef } from 'react';
import { EditorProvider, useEditor, useSelectedPage, useCanUndo, useCanRedo } from './EditorContext';
import EditorToolbar from './EditorToolbar';
import PageList from './PageList';
import PageCanvas from './PageCanvas';
import LayoutPicker from './LayoutPicker';
import EntryPicker from './EntryPicker';
import useBookDraft from './useBookDraft';
import { 
  Loader2, 
  AlertCircle, 
  Save,
  CheckCircle 
} from 'lucide-react';

// ============================================
// MAIN EDITOR COMPONENT
// ============================================

function BookEditorInner({ 
  draftId, 
  babyBookId,
  onComplete,
  onBack 
}) {
  const { 
    state, 
    loadDraft, 
    undo, 
    redo, 
    setSaving, 
    setSaveSuccess, 
    setSaveError,
    toggleLayoutPicker,
    toggleEntryPicker,
    updateEntryCache  // ← ADD THIS: Get the action to update entry cache
  } = useEditor();
  
  const selectedPage = useSelectedPage();
  const canUndo = useCanUndo();
  const canRedo = useCanRedo();
  
  const { 
    draft, 
    loading, 
    error, 
    saveDraft, 
    fetchEntryData,
    entryCache: hookEntryCache  // ← ADD THIS: Get the cache from useBookDraft
  } = useBookDraft(draftId, babyBookId);
  
  // Debounce timer ref
  const saveTimerRef = useRef(null);
  
  // Load draft on mount
  useEffect(() => {
    if (draft && draft.pages) {
      loadDraft(draft.id, draft.pages);
    }
  }, [draft, loadDraft]);
  
  // ============================================
  // FIX: Sync entry cache from useBookDraft to EditorContext
  // This runs after draft loads and whenever hookEntryCache updates
  // ============================================
  useEffect(() => {
    if (hookEntryCache && Object.keys(hookEntryCache).length > 0) {
      updateEntryCache(hookEntryCache);
    }
  }, [hookEntryCache, updateEntryCache]);
  
  // ============================================
  // FIX: Wrapped fetchEntryData to sync results to context
  // ============================================
  const handleFetchEntries = useCallback(async (entryIds) => {
    const entries = await fetchEntryData(entryIds);
    if (entries && Object.keys(entries).length > 0) {
      updateEntryCache(entries);
    }
    return entries;
  }, [fetchEntryData, updateEntryCache]);
  
  // Auto-save when dirty
  useEffect(() => {
    if (state.isDirty && state.status === 'ready') {
      // Clear existing timer
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
      }
      
      // Set new timer (2 second debounce)
      saveTimerRef.current = setTimeout(async () => {
        try {
          setSaving();
          await saveDraft(state.pages);
          setSaveSuccess();
        } catch (err) {
          console.error('Auto-save failed:', err);
          setSaveError(err.message);
        }
      }, 2000);
    }
    
    return () => {
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
      }
    };
  }, [state.isDirty, state.pages, state.status, saveDraft, setSaving, setSaveSuccess, setSaveError]);
  
  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Cmd/Ctrl + Z = Undo
      if ((e.metaKey || e.ctrlKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        if (canUndo) undo();
      }
      // Cmd/Ctrl + Shift + Z = Redo
      if ((e.metaKey || e.ctrlKey) && e.key === 'z' && e.shiftKey) {
        e.preventDefault();
        if (canRedo) redo();
      }
      // Cmd/Ctrl + Y = Redo (alternative)
      if ((e.metaKey || e.ctrlKey) && e.key === 'y') {
        e.preventDefault();
        if (canRedo) redo();
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [canUndo, canRedo, undo, redo]);
  
  // Handle continue to next step
  const handleContinue = useCallback(async () => {
    // Save before continuing
    if (state.isDirty) {
      try {
        setSaving();
        await saveDraft(state.pages);
        setSaveSuccess();
      } catch (err) {
        setSaveError(err.message);
        return; // Don't proceed if save fails
      }
    }
    onComplete(draftId);
  }, [state.isDirty, state.pages, draftId, saveDraft, setSaving, setSaveSuccess, setSaveError, onComplete]);
  
  // Loading state
  if (loading || state.status === 'loading') {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-ocean-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading your book...</p>
        </div>
      </div>
    );
  }
  
  // Error state
  if (error || state.status === 'error') {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center max-w-md">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Unable to load book
          </h3>
          <p className="text-gray-600 mb-4">
            {error || state.error || 'An unexpected error occurred'}
          </p>
          <button
            onClick={onBack}
            className="px-4 py-2 bg-ocean-600 text-white rounded-lg hover:bg-ocean-700"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="flex flex-col h-full bg-gray-100">
      {/* Toolbar */}
      <EditorToolbar 
        onBack={onBack}
        onContinue={handleContinue}
        saveStatus={state.status}
        isDirty={state.isDirty}
        pageCount={state.pages.length}
      />
      
      {/* Main editor area */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left panel - Page list */}
        <div className="w-48 md:w-56 lg:w-64 bg-white border-r border-gray-200 overflow-hidden flex flex-col">
          <div className="p-3 border-b border-gray-200">
            <h3 className="text-sm font-medium text-gray-700">
              Pages ({state.pages.length})
            </h3>
          </div>
          <PageList pages={state.pages} entryCache={state.entryCache} />
        </div>
        
        {/* Center - Page canvas */}
        <div className="flex-1 overflow-auto p-4 md:p-6 lg:p-8">
          <PageCanvas 
            page={selectedPage} 
            entryCache={state.entryCache}
            onFetchEntries={handleFetchEntries}  // ← CHANGED: Use wrapped handler
          />
        </div>
        
        {/* Right panel - Layout picker (desktop only) */}
        <div className="hidden lg:block w-64 bg-white border-l border-gray-200 overflow-auto">
          <LayoutPicker page={selectedPage} />
        </div>
      </div>
      
      {/* Mobile layout picker (bottom sheet) */}
      {state.isLayoutPickerOpen && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div 
            className="absolute inset-0 bg-black/50"
            onClick={() => toggleLayoutPicker(false)}
          />
          <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl max-h-[70vh] overflow-auto">
            <LayoutPicker page={selectedPage} isMobile />
          </div>
        </div>
      )}
      
      {/* Entry picker modal */}
      {state.isEntryPickerOpen && (
        <EntryPicker 
          page={selectedPage}
          babyBookId={babyBookId}
          onClose={() => toggleEntryPicker(false)}
        />
      )}
      
      {/* Save indicator */}
      <SaveIndicator status={state.status} isDirty={state.isDirty} />
    </div>
  );
}

// ============================================
// SAVE INDICATOR
// ============================================

function SaveIndicator({ status, isDirty }) {
  if (status === 'saving') {
    return (
      <div className="fixed bottom-4 right-4 flex items-center gap-2 bg-white px-3 py-2 rounded-full shadow-lg text-sm">
        <Loader2 className="w-4 h-4 animate-spin text-ocean-600" />
        <span className="text-gray-600">Saving...</span>
      </div>
    );
  }
  
  if (!isDirty && status === 'ready') {
    return (
      <div className="fixed bottom-4 right-4 flex items-center gap-2 bg-white px-3 py-2 rounded-full shadow-lg text-sm opacity-50">
        <CheckCircle className="w-4 h-4 text-green-600" />
        <span className="text-gray-600">Saved</span>
      </div>
    );
  }
  
  return null;
}

// ============================================
// WRAPPER WITH PROVIDER
// ============================================

export default function BookEditor(props) {
  return (
    <EditorProvider>
      <BookEditorInner {...props} />
    </EditorProvider>
  );
}
